import { Box, Slider, Typography, useTheme, Paper } from "@mui/material";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import { scaleOrdinal } from "d3-scale";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuBox, LuChevronDown, LuLeaf, LuRefreshCw, LuZap } from "react-icons/lu";
import { useQueryClient } from "@tanstack/react-query";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import EquivalencyStrip from "../../../components/custom/EquivalencyStrip";
import QuickStatsPanel from "../../../components/custom/QuickStatsPanel";
import RadialOrbitalTimeline from "../../../components/custom/RadialOrbitalTimeline";
import SystemBoundary from "../../../environment-page-components/SystemBoundary";
import muiSytle from "../../../style/components-style/muiStyle";
import "../../../style/pages-style/buildings/ResultTab.css";
import { useColorMode } from "../../../theme/ThemeProviderWrapper";
import BuildingInsight from "./BuildingInsight";
import SankeyBreakdown from "./SankeyBreakdown";
// Cached hooks
import { useBuildingProcessData, useBuildingResults } from "../../../utilities/useBuildingQueries";
import { getOrgTokens, ORG_PALETTE } from "../../../theme/style/organizationPalette";

const COLORS = {
  Embodied: "#ef4444",
  Operational: "#f59e0b",
  Biogenic: "#10b981",
  Product: "#3b82f6",
  Gray: "#64748b",
};
const SECTION_COLORS = {
  Substructure: "#0ea5e9",
  substructure: "#0ea5e9",
  Interiors: "#d946ef",
  interiors: "#d946ef",
  Shell: "#14b8a6",
  shell: "#14b8a6",
  Services: "#f97316",
  services: "#f97316",
  Other: "#94a3b8",
};

const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const getCategoryLabel = (cat) => {
  if (cat?.toLowerCase() === "whole_building") return "Operational Carbon";
  return capitalize(cat);
};

const getSubcategoryLabel = (cat, sub) => {
  if (cat?.toLowerCase() === "whole_building") {
    if (sub?.toLowerCase() === "b6") return "Operational Energy use";
    if (sub?.toLowerCase() === "b7") return "Operational Water use";
  }
  return capitalize(sub);
};

// Number formatting helpers
const formatNumber = (num) => {
  if (!num || num === 0) return "0";
  const absNum = Math.abs(num);
  if (absNum >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  } else if (absNum >= 1000) {
    return (num / 1000).toFixed(1) + "k";
  }
  return Math.round(num).toString();
};
const toTons = (kg) => kg / 1000;

const formatNumberNoDecimals = (num) => {
  return Math.round(num).toLocaleString();
};

// OLD useResultsData hook removed - now using cached useBuildingResults from useBuildingQueries.js

const SectionAccordion = ({ title, children, icon }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper variant="Table Container" sx={{ mb: 0 }}>
      <TableHeaderContainer>
        <TableTitleContainer>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {icon && <Box sx={{ color: isDark ? "#4ade80" : "#10b981", display: "flex" }}>{icon}</Box>}
            <Typography variant="Table Title">{title}</Typography>
          </Box>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    </Paper>
  );
};

const MetricCard = ({ title, value, unit, color, icon, bgColor }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Use vibrant solid colors in dark mode, matching the GWP cards
  const finalBg = isDark ? color : bgColor;
  const finalTextColor = isDark ? "#ffffff" : "inherit";
  const finalMetricColor = isDark ? "#ffffff" : color;

  return (
    <div className="metric-card" style={{ backgroundColor: finalBg, border: `1px solid ${color}`, color: finalTextColor }}>
      <div className="metric-icon-box" style={{ color: finalMetricColor }}>
        {React.cloneElement(icon, { size: 32, style: { color: "currentColor" } })}
      </div>
      <div>
        <div className="metric-title" style={{ color: isDark ? "rgba(255,255,255,0.8)" : undefined }}>
          {title}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
          <span className="metric-value" style={{ color: finalMetricColor }}>
            {value}
          </span>
          <span className="metric-unit" style={{ color: isDark ? "rgba(255,255,255,0.7)" : undefined }}>
            {unit}
          </span>
        </div>
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{payload[0].payload.name || label}</p>
        <p className="tooltip-value">
          {payload[0].value.toLocaleString()} <span className="unit">kgCO₂e</span>
        </p>
      </div>
    );
  }
  return null;
};


const ResultTab = ({ building }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const serviceLife = building?.service_life || 0;
  const extendedLife = building?.extended_service_life || 0;
  const maxDuration = serviceLife + extendedLife;
  const [filter, setFilter] = useState({ category: "Global", subcategory: null });
  // CACHED: LCA Results and Process Data
  const { data, isLoading: loading } = useBuildingResults(building?.id, filter);
  const { data: processData = [] } = useBuildingProcessData(building?.id);

  const [tab, setTab] = useState("sections");
  const [duration, setDuration] = useState(5);
  const [equivalencyMode, setEquivalencyMode] = useState("trees");
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDark = theme.palette.mode === "dark";

  const handleFilter = (c, s = null) => setFilter({ category: c, subcategory: s });

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (building?.id) {
      setIsRefreshing(true);
      // Invalidate building-related queries
      await queryClient.invalidateQueries({ queryKey: ["building", building.id] });
      // Short delay to show animation if it's too fast
      setTimeout(() => setIsRefreshing(false), 1000);
    }
  };

  // --- DATA PROCESSING ---
  const lcaData = useMemo(() => {
    if (!data) return [];
    const stages = data.lcaGroups || data.lcaStages || [];
    const chartMap = Object.fromEntries(stages.map((i) => [i.stage_group, i]));
    const total = stages.reduce((acc, curr) => acc + Math.abs(curr.value), 0);

    return [
      { key: "A1-A5", label: "A1-A5", color: "#3b82f6" },
      { key: "B1-B5", label: "B1-B5", color: "#10b981" },
      { key: "B6-B7", label: "B6-B7", color: "#f59e0b" },
      { key: "C1-C4", label: "C1-C4", color: "#ef4444" },
      { key: "D", label: "D", color: "#8b5cf6" },
    ].map((c) => {
      let val = chartMap[c.key]?.value ?? 0;
      // If we're looking for B6-B7 but have separate B6 and B7 from backend, sum them
      if (c.key === "B6-B7" && val === 0) {
        val = (chartMap["B6"]?.value ?? 0) + (chartMap["B7"]?.value ?? 0);
      }
      const absVal = Math.abs(val);
      const pct = total > 0 ? Math.round((absVal / total) * 100) : 0;
      return {
        stage: c.label,
        name: c.label,
        value: val, // Keep original value for bar chart (can be negative)
        absValue: absVal, // Use for pie charts
        color: c.color,
        pct: Math.min(pct, 100),
      };
    });
  }, [data]);

  // Fix for Pie Chart: Use absolute values to avoid 101% / -1% issues
  const lcaPieData = lcaData
    .filter((d) => Math.abs(d.value) > 1)
    .map((d) => ({
      ...d,
      value: Math.abs(d.value),
    }));

  const donutData = useMemo(
    () =>
      data?.buildingSections?.map((s, i) => ({
        name: getCategoryLabel(s.category),
        value: Math.abs(s.subs.reduce((a, b) => a + b.impact, 0)), // Use absolute value
        originalValue: s.subs.reduce((a, b) => a + b.impact, 0),
        color: SECTION_COLORS[s.category] || COLORS.Gray,
      })) || [],
    [data]
  );

  // Recalculate percentages for detailed breakdown to ensure consistency
  const processedSections = useMemo(() => {
    if (!data?.buildingSections) return [];
    const totalImpact = data.buildingSections.reduce((acc, sec) => acc + sec.subs.reduce((sAcc, sub) => sAcc + Math.abs(sub.impact), 0), 0);

    return data.buildingSections.map((sec) => ({
      ...sec,
      subs: sec.subs.map((sub) => ({
        ...sub,
        pct: totalImpact > 0 ? Math.round((Math.abs(sub.impact) / totalImpact) * 100) : 0,
      })),
    }));
  }, [data]);

  const hotspotData = useMemo(() => {
    if (!data || !data.materials) return [];
    // Hardcoded schemeCategory10 colors to avoid import issues
    const colors = ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf"];
    const colorScale = scaleOrdinal(colors);
    return data.materials.slice(0, 10).map((m, i) => {
      const materialName = m.material || "Unknown Material";
      return {
        name: materialName.length > 25 ? materialName.substring(0, 25) + "..." : materialName,
        fullName: materialName,
        impact: m.impact,
        pct: m.pct,
        fill: colorScale(i),
      };
    });
  }, [data]);

  const embodied = data?.embodiedCarbon ?? data?.metrics?.embodiedCarbon ?? 0;
  const operational = data?.operationalCarbon ?? data?.metrics?.operationalCarbon ?? 0;
  const biogenic = data?.biogenicCarbon ?? data?.metrics?.biogenicCarbon ?? 0;

  const totalCarbon = embodied + operational;
  const embodiedPct = totalCarbon ? Math.round((embodied / totalCarbon) * 100) : 0;
  const operationalPct = totalCarbon ? Math.round((operational / totalCarbon) * 100) : 0;

  const filterOptions = data?.structure || data?.filterOptions || {};

  // --- CUMULATIVE PROJECTION DATA ---
  const projectionData = useMemo(() => {
    const startYear = building?.Year || building?.year || 2025;
    const embodiedVal = embodied || 0;
    const operationalVal = operational || 0;

    const proj = [];
    for (let i = 0; i <= duration; i++) {
      const currentYear = startYear + i;
      // Year 0 (Start): Embodied only.
      // Year 1+: Embodied + (Annual Operational * Years since start)
      const cumulativeOp = i * operationalVal;

      proj.push({
        year: currentYear,
        embodied: embodiedVal,
        operational: cumulativeOp, // Renamed from operational_cumulative
        total: embodiedVal + cumulativeOp,
      });
    }
    return proj;
  }, [embodied, operational, duration]);

  // Filter process data for System Boundary based on Analysis Scope
  const filteredProcessData = useMemo(() => {
    if (!processData || processData.length === 0) return [];
    if (!filter || filter.category === "Global") return processData;

    const { category, subcategory } = filter;
    const catLower = category.toLowerCase();

    return processData.filter((item) => {
      const itemCat = item.building_section_category;
      const itemSub = item.building_section_subcategory;
      const stage = item.lca_stage || "";

      // 1. Category Filter logic (matching backend get_results)
      let categoryMatch = false;
      if (catLower === "whole_building") {
        categoryMatch = itemCat?.toLowerCase() === "whole_building" || stage.startsWith("B6") || stage.startsWith("B7");
      } else {
        categoryMatch = itemCat === category;
      }

      if (!categoryMatch) return false;

      // 2. Subcategory Filter logic
      if (subcategory) {
        if (catLower === "whole_building") {
          return stage.startsWith(subcategory);
        } else {
          return itemSub === subcategory;
        }
      }

      return true;
    });
  }, [processData, filter]);

  return (
    <Box className="result-tab-container" sx={{ display: "flex", flexDirection: "column", gap: ORG_PALETTE.spacing.sectionGap }}>
      {/* HEADER */}
      <div className="header-wrapper">
        <div className="header">
          <h2 className="header-title">
            {t("LCA Results")} <span className="header-subtitle">/ {building?.name}</span>
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div className="header-badge">{t("Updated:")} {new Date().toLocaleDateString()}</div>
            <button
              onClick={handleRefresh}
              className={`refresh-btn ${isRefreshing ? "loading" : ""}`}
              title={t("Refresh Data")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "white",
                color: isDark ? "#94a3b8" : "#64748b",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              <LuRefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* COMPONENT 1: ANALYSIS SCOPE */}
      <SectionAccordion title={t("Analysis Scope")} defaultExpanded={true}>
        <div className="control-panel">
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <span className="filter-label" style={{ color: isDark ? "#94a3b8" : "#475569" }}>{t("Select Scope:")}</span>
            <button onClick={() => handleFilter("Global")} className={`filter-chip ${filter.category === "Global" ? "active" : ""}`}>
              {t("Global View")}
            </button>
            {filterOptions &&
              Object.keys(filterOptions).map((cat) => (
                <button key={cat} onClick={() => handleFilter(cat)} className={`filter-chip ${filter.category === cat ? "active" : ""}`}>
                  {getCategoryLabel(cat)}
                </button>
              ))}
          </div>

          {filter.category !== "Global" && filterOptions?.[filter.category]?.length > 0 && (
            <div className="sub-filter-container">
              <span className="sub-filter-label">{t("Refine Selection:")}</span>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button onClick={() => handleFilter(filter.category, null)} className={`sub-chip ${!filter.subcategory ? "active" : ""}`}>
                  {t("All")}
                </button>
                {filterOptions[filter.category].map((sub) => (
                  <button key={sub} onClick={() => handleFilter(filter.category, sub)} className={`sub-chip ${filter.subcategory === sub ? "active" : ""}`}>
                    {getSubcategoryLabel(filter.category, sub)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </SectionAccordion>

      {/* COMPONENT 2: IMPACT OVERVIEW */}
      <SectionAccordion title={t("Environmental Impact Overview")} loading={loading} collapseOnLoad={true}>
        <div style={{ display: "flex", gap: "24px", width: "100%", alignItems: "flex-start", justifyContent: "flex-start" }}>
          {/* Left Side: Radial Timeline */}
          <div style={{ width: "520px", flexShrink: 0, display: "flex", justifyContent: "center" }}>
            <RadialOrbitalTimeline data={data} building={building} filter={filter} />
          </div>

          {/* Right Side: Data Panel */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px", minWidth: 0 }}>
            {data && (
              <QuickStatsPanel
                filter={filter}
                timelineData={[
                  { title: "Total Carbon", value: totalCarbon, isCenter: true },
                  { title: "Embodied Carbon", value: embodied, color: COLORS.Embodied },
                  {
                    title: "Operational Carbon",
                    value: operational,
                    color: COLORS.Operational,
                    subNodes: [
                      { id: "B6", value: Math.abs(data?.lcaGroups?.find((s) => (s.stage_group || s.stage || "").startsWith("B6"))?.value || 0) },
                      { id: "B7", value: Math.abs(data?.lcaGroups?.find((s) => (s.stage_group || s.stage || "").startsWith("B7"))?.value || 0) },
                    ],
                  },
                ]}
              />
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <MetricCard title={t("Total Carbon")} value={formatNumberNoDecimals(totalCarbon)} unit="kgCO₂e" color="#3b82f6" bgColor="#eff6ff" icon={<LuLeaf />} />
              <MetricCard title={t("Biogenic Storage")} value={formatNumberNoDecimals(biogenic)} unit="kgCO₂e" color={COLORS.Biogenic} bgColor="#f0fdf4" icon={<LuLeaf />} />
              <MetricCard title={t("Embodied")} value={formatNumberNoDecimals(embodied)} unit="kgCO₂e" color={COLORS.Embodied} bgColor="#fef2f2" icon={<LuBox />} />
              {(filter.category === "Global" || filter.category?.toLowerCase() === "whole_building") && (
                <MetricCard title={t("Operational")} value={formatNumberNoDecimals(operational)} unit="kgCO₂e" color={COLORS.Operational} bgColor="#fff7ed" icon={<LuZap />} />
              )}
            </div>

            <EquivalencyStrip totalCarbon={totalCarbon} equivalencyMode={equivalencyMode} setEquivalencyMode={setEquivalencyMode} />
          </div>
        </div>
      </SectionAccordion>

      {/* COMPONENT 4: DETAILED BREAKDOWN */}
      <SectionAccordion title={t("Detailed Breakdown")} loading={loading} collapseOnLoad={true}>
        <div className="tab-buttons">
          <button onClick={() => setTab("overview")} className={`tab-button ${tab === "overview" ? "active" : ""}`}>
            {t("Overview")}
          </button>
          <button onClick={() => setTab("sections")} className={`tab-button ${tab === "sections" ? "active" : ""}`}>
            {t("Sections")}
          </button>
          <button onClick={() => setTab("materials")} className={`tab-button ${tab === "materials" ? "active" : ""}`}>
            {t("Materials")}
          </button>
        </div>

        {/* TAB 1: OVERVIEW */}
        {tab === "overview" && (
          <div className="split-layout">
            <div className="chart-box">
              <h4 className="chart-title">{t("LCA Stages")}</h4>
              <div style={{ width: "100%", height: 300, minHeight: 300 }}>
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={lcaData} margin={{ top: 20, right: 0, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} dy={10} />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      fontSize={11}
                      tick={{ fill: "#64748b" }}
                      label={{ value: "GWP (kgCO₂e)", angle: -90, position: "insideLeft", style: { textAnchor: "middle", fill: "#94a3b8", fontSize: 11 } }}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={50}>
                      {lcaData.map((e, i) => (
                        <Cell key={i} fill={e.color} />
                      ))}
                      <LabelList dataKey="value" position="insideTop" fill="#fff" formatter={formatNumber} style={{ fontSize: 11, fontWeight: 600 }} />
                      <LabelList dataKey="pct" position="top" formatter={(val) => (val > 0 ? `${val}%` : "")} style={{ fontSize: 11, fill: "#64748b", fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="chart-box">
              <h4 className="chart-title">{t("Impact Ratio")}</h4>
              <div style={{ display: "flex", alignItems: "center", height: "300px" }}>
                <div style={{ position: "relative", width: "60%", height: 300, minHeight: 300 }}>
                  <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none", zIndex: 10 }}>
                    <div style={{ fontSize: "14px", color: isDark ? "#94a3b8" : "#64748b", marginBottom: "4px" }}>{t("Total GWP")}</div>
                    <div style={{ fontSize: "24px", fontWeight: "800", color: isDark ? "#f1f5f9" : "#334155" }}>{formatNumberNoDecimals(embodied + operational)}</div>
                    <div style={{ fontSize: "12px", color: "#94a3b8" }}>kgCO₂e</div>
                  </div>
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <PieChart>
                      <Pie
                        data={lcaPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={80}
                        outerRadius={110}
                        paddingAngle={2}
                        stroke="none"
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                        {lcaPieData.map((e, i) => (
                          <Cell key={i} fill={e.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ width: "40%", paddingLeft: "10px" }}>
                  {lcaPieData.map((entry, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                      <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: entry.color, marginRight: "8px" }}></div>
                      <span style={{ fontSize: "13px", color: "#475569", fontWeight: 500 }}>{entry.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: SECTIONS */}
        {tab === "sections" && (
          <div className="split-layout">
            <div className="chart-box">
              <h4 className="chart-title">{filter.category === "Global" ? t("Building Sections Split") : t("Category Split")}</h4>
              <div style={{ display: "flex", alignItems: "center", height: "320px" }}>
                <div style={{ position: "relative", width: "60%", height: 320, minHeight: 320 }}>
                  <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none", zIndex: 10 }}>
                    <div style={{ fontSize: "14px", color: isDark ? "#94a3b8" : "#64748b", marginBottom: "4px" }}>{t("Total Impact")}</div>
                    <div style={{ fontSize: "24px", fontWeight: "800", color: isDark ? "#f1f5f9" : "#334155" }}>{formatNumberNoDecimals(embodied + operational)}</div>
                    <div style={{ fontSize: "12px", color: "#94a3b8" }}>kgCO₂e</div>
                  </div>
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={90}
                        outerRadius={120}
                        paddingAngle={2}
                        stroke="none"
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                        {donutData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ width: "40%", paddingLeft: "20px" }}>
                  {donutData.map((entry, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                      <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: entry.color, marginRight: "8px" }}></div>
                      <span style={{ fontSize: "13px", color: "#475569", fontWeight: 500 }}>{entry.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="details-box">
              <h4 className="chart-title">{t("Detailed Breakdown")}</h4>
              <div className="scrollable-list">
                {processedSections?.map((sec, sIdx) => (
                  <div key={`${sec.category}-${sIdx}`} className="section-group">
                    <div className="section-header" style={{ borderLeft: `4px solid ${SECTION_COLORS[sec.category] || "#999"}` }}>
                      {getCategoryLabel(sec.category)}
                    </div>
                    {sec.subs.map((s, iIdx) => (
                      <div key={`${s.name}-${iIdx}`} className="section-row">
                        <span className="sub-name">{getSubcategoryLabel(sec.category, s.name)}</span>
                        <div style={{ display: "flex", gap: "10px" }}>
                          <span style={{ fontWeight: 600 }}>{formatNumberNoDecimals(s.impact)}</span>
                          <span style={{ color: "#999", width: "40px", textAlign: "right" }}>{s.pct}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: MATERIALS */}
        {tab === "materials" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
            <div className="chart-box">
              <h4 className="chart-title" style={{ marginBottom: 0 }}>
                {" "}
                {t("Material Hotspots")}{" "}
              </h4>
              <div style={{ width: "100%", height: 500, minHeight: 500 }}>
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart layout="vertical" data={hotspotData} margin={{ top: 20, right: 50, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                    <XAxis type="number" label={{ value: "Global Warming Potential (kgCO₂e)", position: "insideBottom", offset: -10, fontSize: 12, fill: "#64748b" }} />
                    <YAxis dataKey="name" type="category" width={220} tick={{ fontSize: 12, fill: "#475569" }} />
                    <Tooltip cursor={{ fill: "#f8fafc" }} content={<CustomTooltip />} />
                    <Bar dataKey="impact" radius={[0, 4, 4, 0]} barSize={24}>
                      {hotspotData.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                      <LabelList dataKey="impact" position="insideRight" fill="#fff" formatter={(val) => val.toLocaleString()} style={{ fontSize: 11, fontWeight: 600 }} />
                      <LabelList dataKey="pct" position="right" formatter={(val) => `${val}%`} style={{ fontSize: 11, fill: "#64748b" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="table-container">
              <table className="rt-table">
                <thead>
                  <tr>
                    <th className="rt-th">{t("Material")}</th>
                    <th className="rt-th" style={{ textAlign: "right" }}>
                      {t("Impact")}
                    </th>
                    <th className="rt-th" style={{ textAlign: "right" }}>
                      %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.materials?.map((m, i) => (
                    <tr key={i} className={i % 2 ? "rt-tr-alt" : "rt-tr"}>
                      <td className="rt-td">{m.material}</td>
                      <td className="rt-td" style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatNumberNoDecimals(m.impact)}
                      </td>
                      <td className="rt-td" style={{ textAlign: "right" }}>
                        <span className="pct-badge">{m.pct}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </SectionAccordion>

      {/* COMPONENT 2.5: SYSTEM BOUNDARY */}
      {filteredProcessData && filteredProcessData.length > 0 && <SystemBoundary processData={filteredProcessData} title={t("System Boundary")} isBuilding={true} />}

      {/* COMPONENT 3: CUMULATIVE PROJECTION (NEW) */}
      <SectionAccordion title={t("Cumulative Carbon Projection")} loading={loading} collapseOnLoad={true}>
        <div className="chart-box" style={{ padding: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h4 className="chart-title" style={{ margin: 0 }}>
              {t("Projected Carbon Emissions")}
            </h4>
            <div style={{ width: "200px", display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>{t("Duration:")} {duration} {t("Years")}</span>
              <Slider size="small" value={duration} onChange={(e, v) => setDuration(v)} min={1} max={maxDuration > 0 ? maxDuration : 5} sx={{ color: "#10b981" }} />
            </div>
          </div>

          <div style={{ width: "100%", height: 400, minHeight: 400 }}>
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <AreaChart data={projectionData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorEmbodied" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.Embodied} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={COLORS.Embodied} stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id="colorOperational" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.Operational} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={COLORS.Operational} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="year"
                  label={{ value: "Year", position: "insideBottomRight", offset: -5 }}
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => toTons(v).toFixed(1)}
                  label={{ value: "Cumulative Tons CO₂e", angle: -90, position: "insideLeft", style: { textAnchor: "middle", fill: "#94a3b8", fontSize: 12 } }}
                />
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <Tooltip
                  contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                  formatter={(value, name) => [formatNumberNoDecimals(value) + " kgCO₂e", capitalize(name)]}
                  labelStyle={{ color: "#64748b", marginBottom: "4px" }}
                  itemStyle={{ fontSize: "13px", fontWeight: 500 }}
                />
                <Area type="monotone" dataKey="embodied" stackId="1" stroke={COLORS.Embodied} fill="url(#colorEmbodied)" name="Embodied Carbon" />
                <Area type="monotone" dataKey="operational" stackId="1" stroke={COLORS.Operational} fill="url(#colorOperational)" name="Operational Carbon" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Custom Legend with Totals */}
          <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginTop: "16px", flexWrap: "wrap" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                background: "#fef2f2",
                borderRadius: "8px",
                border: `1px solid ${COLORS.Embodied}30`,
              }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: COLORS.Embodied }}></div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 500 }}>{t("Total Embodied")}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#f1f5f9" : "#1e293b" }}>{formatNumberNoDecimals(projectionData[projectionData.length - 1]?.embodied)} kgCO₂e</span>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                background: "#fff7ed",
                borderRadius: "8px",
                border: `1px solid ${COLORS.Operational}30`,
              }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: COLORS.Operational }}></div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 500 }}>{t("Total Operational")}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#f1f5f9" : "#1e293b" }}>{formatNumberNoDecimals(projectionData[projectionData.length - 1]?.operational)} kgCO₂e</span>
              </div>
            </div>
          </div>
        </div>
      </SectionAccordion>

      <SectionAccordion title={t("Sankey Breakdown")} loading={loading} collapseOnLoad={true}>
        <SankeyBreakdown data={data} />
      </SectionAccordion>

      <SectionAccordion title={t("LCA Interpretation")}>
        <BuildingInsight buildingId={building?.id} />
      </SectionAccordion>
    </Box>
  );
};

export default ResultTab;
